import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { restartableTask } from 'ember-concurrency';
import type AccessEventModel from 'garaje/models/access-event';
import type StateService from 'garaje/services/state';
import type StoreService from 'garaje/services/store';

interface AccessEventsCorroboratingSignalsArgs {
  accessEvent: AccessEventModel;
}

/**
 * Surfaces the presence signals that corroborated a multi-signal sign-in (Presence Heuristics M1).
 * Renders a trigger only for sign-ins the policy actually corroborated (those carry a
 * `corroboratedEntryId`); opening it fetches exactly the corroborating set — the held signals plus the
 * tipping sign-in — via the badge-log `corroborated-entry-ids` filter, and lists them in a drawer.
 */
export default class AccessEventsCorroboratingSignals extends Component<AccessEventsCorroboratingSignalsArgs> {
  @service declare store: StoreService;
  @service declare state: StateService;

  @tracked isOpen = false;
  @tracked signals: AccessEventModel[] = [];

  // corroboratedEntryId is stamped on every signal in the corroboration set (the held signals AND the
  // tipping sign-in), so gate additionally on createdEntry to render the trigger once — on the sign-in
  // row only, not on each held signal. (A plain createdEntry/entryId check can't do this: it's true for
  // every ordinary sign-in too; corroboratedEntryId is what marks it as multi-signal corroborated.)
  get isCorroboratedSignIn(): boolean {
    return Boolean(this.args.accessEvent.corroboratedEntryId) && Boolean(this.args.accessEvent.createdEntry);
  }

  loadSignals = restartableTask(async () => {
    const { corroboratedEntryId } = this.args.accessEvent;
    if (!corroboratedEntryId) return;
    const results = await this.store.query('access-event', {
      filter: {
        'location-ids': this.state.currentLocation.id,
        'corroborated-entry-ids': corroboratedEntryId,
      },
      page: { limit: 50, offset: 0 },
    });
    this.signals = results.slice();
  });

  @action
  open(): void {
    this.isOpen = true;
    void this.loadSignals.perform();
  }

  @action
  close(): void {
    this.isOpen = false;
  }
}
